// Consolidated monthly customer statement (delivery-side): A4 print view of
// opening balance, month invoices, payments received and closing outstanding.
import { printHtml } from '@/lib/pumpPrint';

const num = (v) => Number(v || 0);
const inr = (v) => `Rs ${num(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const d = (v) => {
  const dt = new Date(v);
  return Number.isNaN(dt.getTime()) ? esc(v) : dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

export const buildCustomerStatementHtml = (s) => {
  const c = s.customer || {};
  const invRows = (s.invoices || [])
    .map((i) => `<tr><td>${d(i.date)}</td><td>${esc(i.invoice_no)}</td><td>${esc(i.order_no || '-')}</td><td>${esc(i.status || '-')}</td><td class="r">${inr(i.amount)}</td><td class="r">${inr(i.paid)}</td><td class="r">${inr(i.balance)}</td></tr>`)
    .join('');
  const payRows = (s.payments || [])
    .map((p) => `<tr><td>${d(p.date)}</td><td>${esc(p.invoice_no || '-')}</td><td>${esc(p.method || '-')}</td><td class="r">${inr(p.amount)}</td></tr>`)
    .join('');
  const closing = num(s.closing_balance);
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Statement — ${esc(c.company_name)} — ${esc(s.month)}</title>
  <style>
    @page { size: A4; margin: 14mm 12mm; }
    body { font-family: Arial, Helvetica, sans-serif; color: #111; font-size: 11px; margin: 0; }
    .head { text-align: center; border-bottom: 2px solid #0e7490; padding-bottom: 8px; margin-bottom: 10px; }
    .head h1 { margin: 0; font-size: 18px; color: #0e7490; letter-spacing: 1px; }
    .head p { margin: 2px 0 0; font-size: 11px; color: #444; }
    .meta { display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 11px; }
    .meta b { font-size: 12px; }
    h3 { font-size: 12px; margin: 12px 0 4px; padding: 3px 6px; background: #eef6f9; border-left: 3px solid #0e7490; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #bbb; padding: 3px 6px; text-align: left; }
    th { background: #f4f7f8; font-size: 10px; }
    td.r, th.r { text-align: right; }
    tr.b td { font-weight: bold; background: #fafafa; }
    .totals { margin-top: 12px; width: 60%; margin-left: auto; }
    .totals td { padding: 4px 8px; }
    .out td { font-weight: bold; font-size: 13px; color: ${closing > 0 ? '#b91c1c' : '#047857'}; }
    .sig { margin-top: 30px; display: flex; justify-content: space-between; font-size: 11px; }
    .sig span { border-top: 1px solid #333; padding-top: 4px; width: 180px; text-align: center; }
  </style></head><body>
    <div class="head">
      <h1>MOBILE FUEL KART</h1>
      <p>Monthly Statement — <b>${monthLabel(s.month)}</b> · Generated: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
    </div>
    <div class="meta">
      <div>
        <b>${esc(c.company_name)}</b><br/>
        ${c.contact_person ? `${esc(c.contact_person)}<br/>` : ''}
        ${c.billing_address ? `${esc(c.billing_address)}<br/>` : ''}
        ${c.gstin ? `GSTIN: ${esc(c.gstin)}<br/>` : ''}
        ${c.phone ? `Phone: ${esc(c.phone)}` : ''}
      </div>
      <div style="text-align:right">
        Payment Terms: <b>${esc(c.payment_terms || '-')}</b><br/>
        Opening Balance: <b>${inr(s.opening_balance)}</b>
      </div>
    </div>

    <h3>Invoices (${(s.invoices || []).length})</h3>
    <table>
      <thead><tr><th>Date</th><th>Invoice No</th><th>Order No</th><th>Status</th><th class="r">Amount</th><th class="r">Paid</th><th class="r">Balance</th></tr></thead>
      <tbody>${invRows || '<tr><td colspan="7">No invoices this month</td></tr>'}
      ${invRows ? `<tr class="b"><td colspan="4">TOTAL INVOICED</td><td class="r">${inr(s.invoiced_total)}</td><td colspan="2"></td></tr>` : ''}</tbody>
    </table>

    <h3>Payments Received (${(s.payments || []).length})</h3>
    <table>
      <thead><tr><th>Date</th><th>Against Invoice</th><th>Method</th><th class="r">Amount</th></tr></thead>
      <tbody>${payRows || '<tr><td colspan="4">No payments this month</td></tr>'}
      ${payRows ? `<tr class="b"><td colspan="3">TOTAL PAID</td><td class="r">${inr(s.paid_total)}</td></tr>` : ''}</tbody>
    </table>

    <table class="totals">
      <tbody>
        <tr><td>Opening Balance</td><td style="text-align:right">${inr(s.opening_balance)}</td></tr>
        <tr><td>+ Invoiced this month</td><td style="text-align:right">${inr(s.invoiced_total)}</td></tr>
        <tr><td>− Paid this month</td><td style="text-align:right">${inr(s.paid_total)}</td></tr>
        <tr class="out"><td>CLOSING BALANCE</td><td style="text-align:right">${inr(closing)}</td></tr>
      </tbody>
    </table>

    <div class="sig"><span>Customer Signature</span><span>Authorised Signatory</span></div>
  </body></html>`;
};

export const printCustomerStatement = (statement) => {
  printHtml(buildCustomerStatementHtml(statement));
};

// A4 ledger print: opening balance + every invoice (debit) and payment (credit)
// with a running balance — the customer's full account history on one page.
export const buildCustomerLedgerHtml = (l) => {
  const c = l.customer || {};
  const rows = (l.entries || [])
    .map((e) => `<tr${e.type === 'opening' ? ' class="b"' : ''}><td>${e.date ? d(e.date) : '-'}</td><td>${esc(e.particulars)}</td><td class="r">${e.debit ? inr(e.debit) : '-'}</td><td class="r">${e.credit ? inr(e.credit) : '-'}</td><td class="r">${inr(e.balance)}</td></tr>`)
    .join('');
  const closing = num(l.closing_balance);
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Ledger — ${esc(c.company_name)}</title>
  <style>
    @page { size: A4; margin: 14mm 12mm; }
    body { font-family: Arial, Helvetica, sans-serif; color: #111; font-size: 11px; margin: 0; }
    .head { text-align: center; border-bottom: 2px solid #0e7490; padding-bottom: 8px; margin-bottom: 10px; }
    .head h1 { margin: 0; font-size: 18px; color: #0e7490; letter-spacing: 1px; }
    .head p { margin: 2px 0 0; font-size: 11px; color: #444; }
    .meta { display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 11px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #bbb; padding: 3px 6px; text-align: left; }
    th { background: #f4f7f8; font-size: 10px; }
    td.r, th.r { text-align: right; }
    tr.b td { font-weight: bold; background: #fafafa; }
    .totals { margin-top: 12px; width: 60%; margin-left: auto; }
    .totals td { padding: 4px 8px; }
    .out td { font-weight: bold; font-size: 13px; color: ${closing > 0 ? '#b91c1c' : '#047857'}; }
    .sig { margin-top: 30px; display: flex; justify-content: space-between; font-size: 11px; }
    .sig span { border-top: 1px solid #333; padding-top: 4px; width: 180px; text-align: center; }
  </style></head><body>
    <div class="head">
      <h1>MOBILE FUEL KART</h1>
      <p>Customer Account Ledger · Generated: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
    </div>
    <div class="meta">
      <div>
        <b>${esc(c.company_name)}</b><br/>
        ${c.gstin ? `GSTIN: ${esc(c.gstin)}<br/>` : ''}
        ${c.phone ? `Phone: ${esc(c.phone)}` : ''}
      </div>
      <div style="text-align:right">
        ${l.opening_balance != null ? `Opening Balance: <b>${inr(l.opening_balance)}</b> ${l.opening_balance_date ? `(as of ${d(l.opening_balance_date)})` : ''}` : ''}
      </div>
    </div>
    <table>
      <thead><tr><th>Date</th><th>Particulars</th><th class="r">Debit</th><th class="r">Credit</th><th class="r">Balance</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="5">No ledger entries</td></tr>'}</tbody>
    </table>
    <table class="totals">
      <tbody>
        <tr><td>Total Debits</td><td style="text-align:right">${inr(l.total_debit)}</td></tr>
        <tr><td>Total Credits (paid)</td><td style="text-align:right">${inr(l.total_credit)}</td></tr>
        <tr class="out"><td>OUTSTANDING BALANCE</td><td style="text-align:right">${inr(closing)}</td></tr>
      </tbody>
    </table>
    <div class="sig"><span>Customer Signature</span><span>Authorised Signatory</span></div>
  </body></html>`;
};

export const printCustomerLedger = (ledger) => {
  printHtml(buildCustomerLedgerHtml(ledger));
};
